import { Editor } from '../core/Editor';

export class ShortcutsModal {
  private editor: Editor;
  private container: HTMLDivElement | null = null;

  constructor(editor: Editor) {
    this.editor = editor;
  }

  public open() {
    this.close();

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop';
    overlay.id = 'shortcuts-modal';

    overlay.innerHTML = `
      <div class="modal-card shortcuts-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <div class="modal-icon">⌨️</div>
            <div>
              <h3>Photoshop Keyboard Shortcuts</h3>
              <p class="modal-subtitle">Industry-standard keybindings and accelerator sequences</p>
            </div>
          </div>
          <button class="modal-close-btn" id="close-shortcuts">&times;</button>
        </div>

        <div class="modal-body shortcuts-modal-body">
          <div class="shortcuts-grid">
            <!-- Column 1: Tools & Editing -->
            <div class="shortcuts-col">
              <h4>Tools & Brushes</h4>
              <div class="shortcut-row"><kbd>V</kbd> <span>Move & Transform</span></div>
              <div class="shortcut-row"><kbd>M</kbd> <span>Marquee Selection</span></div>
              <div class="shortcut-row"><kbd>W</kbd> <span>Magic Wand (Select)</span></div>
              <div class="shortcut-row highlight"><kbd>Shift</kbd>+<kbd>W</kbd> <span>Magic Wand: Remove BG & Crop</span></div>
              <div class="shortcut-row highlight"><kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>T</kbd> <span>Trim Transparent Borders</span></div>
              <div class="shortcut-row"><kbd>C</kbd> <span>Crop Tool</span></div>
              <div class="shortcut-row"><kbd>J</kbd> <span>Healing & Clone Stamp</span></div>
              <div class="shortcut-row"><kbd>B</kbd> <span>Brush Tool</span></div>
              <div class="shortcut-row"><kbd>R</kbd> <span>Smudge Tool</span></div>
              <div class="shortcut-row"><kbd>E</kbd> <span>Eraser Tool</span></div>
              <div class="shortcut-row"><kbd>G</kbd> <span>Paint Bucket</span></div>
              <div class="shortcut-row"><kbd>T</kbd> <span>Text Tool</span></div>
              <div class="shortcut-row"><kbd>I</kbd> <span>Eyedropper</span></div>
              <div class="shortcut-row"><kbd>[</kbd> / <kbd>]</kbd> <span>Brush Size Down / Up</span></div>
              <div class="shortcut-row"><kbd>Shift</kbd>+<kbd>[</kbd> / <kbd>]</kbd> <span>Brush Hardness Down / Up</span></div>
              <div class="shortcut-row"><kbd>X</kbd> <span>Swap Colors</span></div>
              <div class="shortcut-row"><kbd>D</kbd> <span>Default B&W Colors</span></div>
            </div>

            <!-- Column 2: Photoshop Operations & Layers -->
            <div class="shortcuts-col">
              <h4>Photoshop Commands</h4>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>C</kbd> <span>Copy Selection or Layer</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>V</kbd> <span>Paste as New Layer</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>X</kbd> <span>Cut Selection</span></div>
              <div class="shortcut-row highlight"><kbd>Alt</kbd>+<kbd>I</kbd>+<kbd>P</kbd> <span>Crop to Selection (Image &rarr; Crop)</span></div>
              <div class="shortcut-row highlight"><kbd>Alt</kbd>+<kbd>Backspace</kbd> <span>Fill with Foreground Color</span></div>
              <div class="shortcut-row highlight"><kbd>Ctrl</kbd>+<kbd>Backspace</kbd> <span>Fill with Background Color</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>J</kbd> <span>Duplicate Layer (Layer via Copy)</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>N</kbd> <span>New Layer</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>E</kbd> <span>Merge Down</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>T</kbd> <span>Free Transform</span></div>
              <div class="shortcut-row"><kbd>0</kbd>–<kbd>9</kbd> <span>Layer Opacity (10% to 100%)</span></div>
            </div>

            <!-- Column 3: Selection, Color & Navigation -->
            <div class="shortcuts-col">
              <h4>Selection & Adjustments</h4>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>A</kbd> <span>Select All</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>D</kbd> <span>Deselect</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>I</kbd> <span>Invert Selection</span></div>
              <div class="shortcut-row"><kbd>Delete</kbd> <span>Clear to Transparent</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>U</kbd> <span>Hue / Saturation / Adjustments</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>I</kbd> <span>Invert Colors</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>U</kbd> <span>Desaturate (Grayscale)</span></div>
              <div class="shortcut-row"><kbd>Space</kbd>+<kbd>Drag</kbd> <span>Pan Canvas (Hand)</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>0</kbd> <span>Fit Canvas on Screen</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>1</kbd> <span>Actual Size (100% Zoom)</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>+</kbd> / <kbd>-</kbd> <span>Zoom In / Out</span></div>
              <div class="shortcut-row"><kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>S</kbd> <span>Save for Web Dialog</span></div>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" id="close-shortcuts-btn">Close</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.container = overlay;

    overlay.querySelector('#close-shortcuts')?.addEventListener('click', () => this.close());
    overlay.querySelector('#close-shortcuts-btn')?.addEventListener('click', () => this.close());
  }

  public close() {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}
